import { getSessionToken } from '@shopify/app-bridge-utils';
import axios from 'axios';

import { baseUrl } from 'config/baseUrl';
import { PATHS } from 'config/constants';
import { handleErrorPayload } from 'utils/handleErrorPayload';
import { getAppBridge, initAppBridge } from 'utils/shopifyNav';

import { getCampaignReportByStatusAdaptor } from 'adaptors/dashboard/status-report.adaptor';
import { orderReport, releaseNote, ruleStats } from 'rest/dist/gen/api/dashboard/dashboard';
import {
  createRule as createRuleApi,
  deleteRule as deleteRuleApi,
  estimateRuleItemsCount as estimateRuleItemsCountApi,
  getRule as getRuleApi,
  getRuleEligibleCustomers as getRuleEligibleCustomersApi,
  getRuleEligibleCustomerSegments as getRuleEligibleCustomerSegmentsApi,
  getRuleEligibleCustomerTags as getRuleEligibleCustomerTagsApi,
  getRules as getRulesApi,
  updateRule as updateRuleApi,
  previewRuleCoverage as previewRuleCoverageApi,
  getRuleCoverage as getRuleCoverageApi,
  getRuleOverlaps as getRuleOverlapsApi,
  excludeRuleProducts as excludeRuleProductsApi,
  resolveRuleProduct as resolveRuleProductApi,
  getRuleCoverageProducts as getRuleCoverageProductsApi,
  previewRuleCoverageProducts as previewRuleCoverageProductsApi,
} from 'rest/dist/gen/api/rules/rules';
import { getPlans as getPlansApi, upgradePlan } from 'rest/dist/gen/api/pricing/pricing';
import { getFeatureAccess as getFeatureAccessApi } from 'rest/dist/gen/api/feature-access/feature-access';
import {
  getFlowStatus as getFlowStatusApi,
  updateFlowStatus as updateFlowStatusApi,
  getFlowActivity as getFlowActivityApi,
  enrichFlowActivity as enrichFlowActivityApi,
  getFlowActivityRuleSummary as getFlowActivityRuleSummaryApi,
} from 'rest/dist/gen/api/flow/flow';
import {
  getBlockedOrderAlertSettings as getBlockedOrderAlertSettingsApi,
  setBlockedOrderAlertSettings as setBlockedOrderAlertSettingsApi,
} from 'rest/dist/gen/api/alerts/alerts';
import {
  blockedValueSummary as blockedValueSummaryApi,
  exportBlockedValueSummaryCsv as exportBlockedValueSummaryCsvApi,
  exportBlockedValueEventsCsv as exportBlockedValueEventsCsvApi,
} from 'rest/dist/gen/api/analytics/analytics';
import {
  getB2bSignals as getB2bSignalsApi,
  getCustomerMetafields as getCustomerMetafieldsApi,
  getMoqDesignSettings as getMoqDesignSettingsApi,
  getMoqPreferences as getMoqPreferencesApi,
  getProductTags,
  getProductTypes,
  getShopInfo,
  getShopMetafields as getShopMetafieldsApi,
  getShopStorage,
  getThemeEnabled,
  getVariantMetafields as getVariantMetafieldsApi,
  getVendors,
  hasThemeAccess,
  setcartEmbedSettings,
  setComingSoonClicked,
  setMoqDesignSettings as setMoqDesignSettingsApi,
  setMoqPreferences as setMoqPreferencesApi,
  setShopStorage,
  exportRulesCsv as exportRulesCsvApi,
  importRulesCsv as importRulesCsvApi,
} from 'rest/dist/gen/api/shops/shops';
import {
  listApiKeys as listApiKeysApi,
  createApiKey as createApiKeyApi,
  revokeApiKey as revokeApiKeyApi,
  listApiAuditLog as listApiAuditLogApi,
} from 'rest/dist/gen/api/api-keys/api-keys';

import { saveRuleAdaptor } from 'adaptors/rule/save.adaptor';
import { coverageAdaptor } from 'adaptors/rule/coverage.adaptor';
import { singleRuleFormAdaptor } from 'adaptors/rule/get.adaptor';
import { getRulesListAdaptor } from 'adaptors/rule/list.adaptor';
import { createPlansAdaptor } from 'adaptors/plans/plans-adaptor';
import { getSettingDataAdaptor, setSettingDataAdaptor } from 'adaptors/setting/setting-adaptors';
import { getSettings, setSettings } from 'rest/dist/gen/api/settings/settings';
import { sendSupportMail } from 'rest/dist/gen/api/supports/supports';
import { getChatwootIdentity as getChatwootIdentityApi } from 'rest/dist/gen/api/chatwoot/chatwoot';

class Api {
  constructor(baseUrl) {
    this.axios = axios.create();
    this.axios.defaults.baseURL = baseUrl;
    this.axios.defaults.timeout = 90000;
    this.axios.defaults.headers['Content-Type'] = 'application/json';
  }

  setAppBridge(host, shopDomain, loggedInAsUser = false) {
    this.loggedInAsUser = loggedInAsUser;

    if (!loggedInAsUser) {
      // Reuse the ONE App Bridge instance owned by utils/shopifyNav — this used
      // to createApp() a second one of its own (and initializeWebVitals on it,
      // in the branch that never ran for embedded merchants). Web vitals
      // reporting now starts inside initAppBridge().
      this.app = initAppBridge(host) || getAppBridge();
    }

    this.axios.interceptors.request.use((config) => {
      if (loggedInAsUser) {
        config.headers['Authorization'] = this._getToken();
        config.headers['admin-login'] = true;
        config.headers['shop-domain'] = this._getShopDomain();
        return config;
      }

      return getSessionToken(this.app || getAppBridge()).then((token) => {
        config.headers['shopify-authorization'] = `Bearer ${token}`;
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['shop-domain'] = shopDomain;
        return config;
      });
    });
  }

  _getToken() {
    let token;
    if ('sessionStorage' in window) token = sessionStorage.getItem('loggedInAsUserToken');
    if (!token) return '';
    // return token;
    return `Bearer ${token}`;
  }

  _getShopDomain() {
    let shopDomain;
    if ('sessionStorage' in window) shopDomain = sessionStorage.getItem('loggedInAsUserShopDomain');
    if (!shopDomain) return '';
    return shopDomain;
  }

  _handleErrors(err) {
    if (err?.response?.status === 401) {
      if (this.loggedInAsUser) {
        window.open(window.location.origin + PATHS.LOGIN + '?message=INVALID_TOKEN', '_self');
      } else {
        // What to do in shopify iframe
      }
    }

    const errorMessage = handleErrorPayload(err);
    return Promise.reject(errorMessage);
  }

  async get(...params) {
    return this.axios.get(...params).catch((err) => this._handleErrors(err));
  }

  async post(...params) {
    return this.axios.post(...params).catch((err) => this._handleErrors(err));
  }

  async patch(...params) {
    return this.axios.patch(...params).catch((err) => this._handleErrors(err));
  }

  async put(...params) {
    return this.axios.put(...params).catch((err) => this._handleErrors(err));
  }

  async delete(...params) {
    return this.axios.delete(...params).catch((err) => this._handleErrors(err));
  }

  // Authentication
  loginAsUser(data) {
    return this.post(`/support/login/panel/`, data);
  }

  // Plans

  async getPlans(params) {
    const res = await getPlansApi(params);

    const raw = res?.data ?? res ?? [];

    return {
      data: {
        success: true,
        data: raw,
        message: 'ok',
      },
    };
  }

  async getPlansAdapted(params) {
    const result = await getPlansApi(params);
    return createPlansAdaptor(result);
  }

  async upgradePlan(params) {
    /** @type {import('rest/dist/gen/models').UpgradePlanDto} */
    var mymodel = {
      planId: params.planId,
      period: params.selectedperiod === 'annually' ? 'annual' : 'monthly',
      discountCode: params.discountCode,
    };
    var result = await upgradePlan(mymodel);
    return result;
    // return this.get(`/billing/updatePlan${params}`);
  }

  async orderRevenueReport(params) {
    const data = await orderReport(params);
    const mapedData = data.data.map((x) => ({ date: x._id, revenue: x.revenue, discount: x.discount ?? 0 }));

    return { data: { data: mapedData, total: data.totalRevenue, success: true, message: 'ok' } };
    // return this.get(`api/auction/orderRevenueReport${params}`);
  }

  async orderCountReport(params) {
    const data = await orderReport(params);
    const mapedData = data.data.map((x) => ({ date: x._id, count: x.count }));

    return { data: { data: mapedData, total: data.totalCount, success: true, message: 'ok' } };
    // return this.get(`api/auction/orderCountReport${params}`);
  }

  async ruleStatsReport(params) {
    const data = await ruleStats(params);
    return {
      data: {
        data: data.data ?? [],
        totalInvalidAttempts: data.totalInvalidAttempts ?? 0,
        totalValidOrders: data.totalValidOrders ?? 0,
        totalContactClicks: data.totalContactClicks ?? 0,
        success: true,
        message: 'ok',
      },
    };
  }

  cancelPlan() {
    return this.get(`/app-subscription/cancel/`);
  }
  getProductList() {
    return this.get(`/api/auction/productList/`);
  }
  getCollectionList() {
    return this.get(`/api/auction/collectionList/`);
  }

  applyDiscount(discountCode) {
    return this.get(`/discounts/${discountCode}/`);
  }

  // Shop Info
  getShopInfo() {
    return this.get('/api/shop/shopInfo');
  }

  // Version x Context x Plan Permission — resolves which FeatureNodes on a
  // surface (e.g. "rules.form") are AVAILABLE / LOCKED_NEEDS_UPGRADE /
  // HIDDEN for the current shop. See useFeatureAccess / FeatureGate and
  // docs/Architecture/version-plan-permission-design.md. Goes through the
  // Orval-generated client (fetchInstance), not a hand-written path — see
  // CLAUDE.md "Every backend request goes through the Orval-generated client".
  async getFeatureAccess(surface) {
    const res = await getFeatureAccessApi({ surface });
    return { data: res?.data ?? res };
  }

  // Shopify Flow automations (docs/Architecture/shopify-flow-spec.md) — the
  // per-shop trigger-emit toggle, Pro-gate status, and this-hour
  // dropped-trigger count for the Automations page.
  async getFlowStatus() {
    const res = await getFlowStatusApi();
    return { data: res?.data ?? res };
  }

  async setFlowAutomationsEnabled(enabled) {
    const res = await updateFlowStatusApi({ enabled });
    return { data: res?.data ?? res };
  }

  // Merchant-facing activity grid (Automations page) — every trigger
  // emission/skip and every inbound action call, newest first.
  async getFlowActivity(limit) {
    const res = await getFlowActivityApi(limit ? { limit } : undefined);
    return { data: res?.data ?? res };
  }

  // On-click enrichment for one activity row — resolves customer/product/
  // order references into display names, fetched live only when opened.
  async enrichFlowActivity(id) {
    const res = await enrichFlowActivityApi(id);
    return { data: res?.data ?? res };
  }

  // Per-rule aggregate view (FlowRuleSummaryModal) — outcome tiles, a
  // why-it's-(not)-firing breakdown, top products, and a daily trend for
  // one rule's Flow trigger activity. Defaults to the last 30 days when no
  // range is passed.
  async getFlowRuleSummary(ruleId, fromDate, toDate) {
    const res = await getFlowActivityRuleSummaryApi({ ruleId, ...(fromDate ? { fromDate } : {}), ...(toDate ? { toDate } : {}) });
    return { data: res?.data ?? res };
  }

  // Pro feature C (docs/shopify-pro-features-spec.md §C) — "email me when a
  // large order is blocked" settings, same allowed/data-null shape as every
  // other Pro-gated endpoint (never a 403 — see the backend's
  // AlertsService header comment).
  async getBlockedOrderAlertSettings() {
    const res = await getBlockedOrderAlertSettingsApi();
    return res?.data ?? res;
  }

  async setBlockedOrderAlertSettings(settings) {
    const res = await setBlockedOrderAlertSettingsApi(settings);
    return res?.data ?? res;
  }

  // Pro feature D (docs/shopify-pro-features-spec.md §D) — "Blocked cart value" reports.
  async blockedValueSummary(params) {
    const res = await blockedValueSummaryApi(params);
    return res?.data ?? res;
  }

  // These three resolve to the raw CSV string. They used to pass a second
  // `{ responseType: 'text' }` argument, which did nothing — the generated
  // functions take only `(params)`, so it was dropped and fetchInstance then
  // ran the CSV through JSON.parse and threw. fetchInstance now picks the
  // parser from the response's Content-Type, so no per-call flag is needed
  // (and passing one would still be ignored).
  async exportBlockedValueSummaryCsv(params) {
    return exportBlockedValueSummaryCsvApi(params);
  }

  async exportBlockedValueEventsCsv(params) {
    return exportBlockedValueEventsCsvApi(params);
  }

  // Pro feature E (docs/shopify-pro-features-spec.md §E) — CSV rule
  // import/export. JSON stays free via the existing safeUninstall.api.js
  // helpers (SafeUninstall.js/Rules.js), left untouched.
  async exportRulesCsv() {
    return exportRulesCsvApi({});
  }

  // NOTE: requires a fresh `npm run orval` to actually send its body. The
  // backend route originally declared an inline `@Body() body: { csv: string }`
  // object literal, which produces no OpenAPI requestBody, so the currently
  // generated `importRulesCsv` is zero-argument and drops `{ csv }` on the
  // floor (an empty POST). Fixed at the source — Prime-MOQ-Backend's
  // src/api/shop/dtos/rules-io.dto.ts (ImportRulesCsvDto) is now wired into
  // shop.controller.ts — so regenerating the client makes this work as
  // written, with no change needed here.
  async importRulesCsv(csv) {
    const res = await importRulesCsvApi({ csv });
    return res?.data ?? res;
  }

  // Pro feature E — public API key management.
  async listApiKeys() {
    const res = await listApiKeysApi();
    return res?.data ?? res;
  }

  async createApiKey(name) {
    const res = await createApiKeyApi({ name });
    return res?.data ?? res;
  }

  async revokeApiKey(id) {
    const res = await revokeApiKeyApi(id);
    return res?.data ?? res;
  }

  async listApiAuditLog() {
    const res = await listApiAuditLogApi();
    return res?.data ?? res;
  }

  // Smart B2B detection (product costs + customer segments) for the dashboard
  // Wholesale & B2B card. Goes through the Orval-generated client; returns
  // { data: { hasCostData, hasSegments } }.
  async getB2bSignals() {
    const res = await getB2bSignalsApi();
    return { data: res?.data ?? { hasCostData: false, hasSegments: false } };
  }
  getShopPlanInfo() {
    return this.get('/api/shop/shopPlanInfo');
  }
  getPermission() {
    return this.get('/api/shop/getPermission/');
  }

  getIsInstalledShop(shop) {
    return this.get('/api/shop/getIsInstalledShop?shop=' + shop);
  }

  // ── Prime MOQ rules ──────────────────────────────────────────────────────
  async createRule(data) {
    const ruleDto = saveRuleAdaptor(data);

    const res = data.id ? await updateRuleApi(data.id, ruleDto) : await createRuleApi(ruleDto);

    const ruleId = data.id ?? res?.data?.id;
    const fresh = await getRuleApi(ruleId);
    const adapted = singleRuleFormAdaptor(fresh.data);

    return { data: { data: adapted, success: true, message: 'ok' } };
  }

  async getRule(ruleId) {
    const res = await getRuleApi(ruleId);
    return { data: singleRuleFormAdaptor(res.data) };
  }

  async getRules(params = {}) {
    const res = await getRulesApi({ pageSize: 200, ...params });
    return { data: getRulesListAdaptor(res) };
  }

  async deleteRule(ruleId) {
    return deleteRuleApi(ruleId);
  }

  async estimateRuleItemsCount(rule) {
    const res = await estimateRuleItemsCountApi(saveRuleAdaptor(rule));
    return res?.data ?? { count: null, variantsCount: 0, products: [] };
  }

  // Rule precedence (docs/rule-precedence-implementation-plan.md §0) — live
  // coverage of an UNSAVED draft: how many of its products it would enforce
  // and which other rules override the rest. Same DTO as a save, so the
  // backend resolves exactly what a save would. A draft may not have a name
  // yet; the DTO requires one, and the name plays no part in coverage.
  async previewRuleCoverage(rule) {
    const res = await previewRuleCoverageApi(saveRuleAdaptor({ ...rule, name: rule.name || 'draft' }));
    return coverageAdaptor(res?.data);
  }

  async getRuleCoverage(ruleId) {
    const res = await getRuleCoverageApi(ruleId);
    return coverageAdaptor(res?.data);
  }

  // Overlaps page (Pro) — docs/rule-precedence-implementation-plan.md §6.8.
  async getRuleOverlaps() {
    const res = await getRuleOverlapsApi();
    return res?.data ?? [];
  }

  // Check a product (Free) — docs/rule-precedence-implementation-plan.md §6.7.
  async resolveRuleProduct(productId) {
    const res = await resolveRuleProductApi({ productId });
    return res?.data ?? { resolvable: true, variantId: null, slots: { qty: null, value: null, weight: null }, losers: [] };
  }

  // Bulk "Exclude these from my rule" (Pro, yield direction only) — docs/
  // rule-precedence-implementation-plan.md §3.5 item 3/§6.1. `products` is
  // [{productId, variantId}], `conflictedRuleId` the rule they currently
  // lose to (drives the "Active elsewhere" marker on the written rows).
  async excludeRuleProducts(ruleId, products, conflictedRuleId) {
    const res = await excludeRuleProductsApi(ruleId, { products, conflictedRuleId });
    const data = res?.data?.data ?? res?.data;
    return { data: singleRuleFormAdaptor(data).data };
  }

  // Full, paginated list of `ruleId`'s own variants that lost to `byRuleId`
  // — the "choose exactly which variants to exclude" modal (Pro). See
  // components/rules/scope/CoverageProductsModal.js.
  async getRuleCoverageProducts(ruleId, byRuleId, cursor, limit) {
    const res = await getRuleCoverageProductsApi(ruleId, { byRuleId, cursor: cursor || undefined, limit: limit || undefined });
    return res?.data ?? { items: [], total: 0, nextCursor: null };
  }

  // Same paginated per-variant list as getRuleCoverageProducts above, but
  // against an UNSAVED draft rule with no id yet: lets CoverageGroups.js's
  // per-group Take over/Exclude buttons open the full item-level picker
  // DURING rule creation, not just after the first save. Same draft-DTO
  // shape previewRuleCoverage() already sends.
  async previewRuleCoverageProducts(rule, byRuleId, cursor, limit) {
    const res = await previewRuleCoverageProductsApi({
      rule: saveRuleAdaptor({ ...rule, name: rule.name || 'draft' }),
      byRuleId,
      cursor: cursor || undefined,
      limit: limit || undefined,
    });
    return res?.data ?? { items: [], total: 0, nextCursor: null };
  }

  async getRuleEligibleCustomers() {
    return getRuleEligibleCustomersApi();
  }

  async getRuleEligibleCustomerSegments() {
    return getRuleEligibleCustomerSegmentsApi();
  }

  async getRuleEligibleCustomerTags() {
    return getRuleEligibleCustomerTagsApi();
  }

  async getTagList() {
    const tags = await getProductTags();
    return { data: { ...tags, success: true } };
    // return this.get(`/api/common/resource/tagList`);
  }
  async getVendorList() {
    const vendors = await getVendors();
    return { data: { ...vendors, success: true } };
    // return this.get(`/api/common/resource/vendorList`);
  }
  async getProductTypeList() {
    const productTypes = await getProductTypes();
    return { data: { ...productTypes, success: true } };
    // return this.get(`/api/common/resource/productTypeList`);
  }
  // Variant (PRODUCTVARIANT) metafield definitions for the campaign rule builder.
  // Goes through the generated Orval client (fetchInstance handles the session
  // token + 403 re-auth), same as getTagList. Backend returns { data: [...] }.
  async getVariantMetafields() {
    const result = await getVariantMetafieldsApi();
    return { data: { ...result, success: true } };
  }
  // Customer metafield definitions (namespace "custom") for
  // LimitSourceField.js's "Customer metafield" dynamic value source picker.
  async getCustomerMetafields() {
    const result = await getCustomerMetafieldsApi();
    return { data: { ...result, success: true } };
  }
  // Shop metafield definitions (namespace "custom") for LimitSourceField.js's
  // "Shop metafield" dynamic value source picker — one value shop-wide.
  async getShopMetafields() {
    const result = await getShopMetafieldsApi();
    return { data: { ...result, success: true } };
  }
  getNotifications(params) {
    return this.get(`/api/common/getNotifications${params}`);
  }
  setNotificationClose(data) {
    return this.post(`/api/common/setNotificationClose/`, data);
  }
  setNotificationView(data) {
    return this.post(`/api/common/setNotificationView/`, data);
  }
  getOption(params) {
    return this.get(`/api/common/getOption${params}`);
  }
  async getPlansWithDiscount(params) {
    const model = { discountCode: params };

    try {
      const res = await getPlansApi(model);
      const raw = res?.data ?? res ?? [];

      return {
        data: {
          success: true,
          data: raw,
          message: 'ok',
        },
      };
    } catch (error) {
      return {
        data: {
          success: false,
          message_key: 'msg_discount_code_not_valid',
          message: 'Discount Code is not Valid',
        },
      };
    }
  }
  async getShopSetting() {
    const settingsData = getSettingDataAdaptor(await getSettings());
    return settingsData;
  }
  async saveShopSetting(data) {
    const saveSettingData = await setSettings(setSettingDataAdaptor(data));
    return saveSettingData;
  }
  async getChatwootIdentity() {
    const res = await getChatwootIdentityApi();
    return res?.data;
  }
  wasItHelpful(data) {
    return this.post(`/api/common/helpfulPage/`, data);
  }
  sendSupportEmail(data) {
    if (data.subject == '') {
      data.subject = 'Campaign creation';
    }
    return sendSupportMail(data);
    // return this.post(`/api/common/sendSupportEmail/`, data);
  }
  async getCampaignReportByStatus() {
    const data = await getCampaignReportByStatusAdaptor();
    return { data };
  }
  getFaqList() {
    return this.get(`/api/common/getFAQList`);
  }

  async getShopStorage(params) {
    const res = await getShopStorage(params);
    const v = res?.data ?? res;
    if (v && typeof v === 'object' && 'data' in v) return { data: v };
    return { data: { data: v } };
  }

  async setShopStorage(items) {
    return setShopStorage(items);
  }

  async getMoqDesignSettings() {
    const res = await getMoqDesignSettingsApi();
    return res?.data ?? res;
  }

  async setMoqDesignSettings(settings) {
    return setMoqDesignSettingsApi(settings);
  }

  async getMoqPreferences() {
    const res = await getMoqPreferencesApi();
    return res?.data ?? res;
  }

  async setMoqPreferences(settings) {
    return setMoqPreferencesApi(settings);
  }
  onboarding(data) {
    /** @type {ShopStorageDto[]} */
    var dtoshopstorage = [];
    if (
      data.onboardingFirstCampaign == true
      // data == 'onboardingFirstCampaign'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingFirstCampaign', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingActivateCampaign == true
      // data == 'onboardingActivateCampaign'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingActivateCampaign', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingNeedUpgradeCampaign !== undefined
      // data == 'onboardingNeedUpgradeCampaign'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = {
        key: 'onboardingNeedUpgradeCampaign',
        value: data.onboardingNeedUpgradeCampaign,
      };
      dtoshopstorage.push(mymodel);
    }
    if (
      // data == 'onboardingThemeExtension'
      data.onboardingThemeExtension == true
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingThemeExtension', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingThemeExtension2 == true
      // data == 'onboardingThemeExtension2'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingThemeExtension2', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingDismiss == true
      // data == 'onboardingDismiss'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingDismiss', value: true };
      dtoshopstorage.push(mymodel);
    }

    if (
      data.onboardingFinalEnableCheck == true
      // data == 'onboardingDismiss'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingFinalEnableCheck', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (data.onboardingVideo == true) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingVideo', value: true };
      dtoshopstorage.push(mymodel);
    }
    return setShopStorage(dtoshopstorage);
    // return this.post(`/api/shop/onboarding/`, data);
  }
  async getCartEmbedSetting() {
    const data = await getShopInfo();
    return { data };
  }
  async setCartEmbedSetting(data) {
    await setcartEmbedSettings(data);

    // return { data: res };
    return { data: { success: true } };
  }

  async getReleaseNotes() {
    const res = await releaseNote();
    return { data: res };
  }

  _toBool(v) {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (s === 'true') return true;
      if (s === 'false') return false;
      if (s === '1') return true;
      if (s === '0') return false;
    }
    return Boolean(v);
  }

  _unwrapData(res) {
    const v = res?.data ?? res;

    if (v && typeof v === 'object' && 'data' in v) return v.data;
    return v;
  }

  async getThemeAccess() {
    const res = await hasThemeAccess(); // GET /api/shops/has_theme_access
    const raw = this._unwrapData(res);
    return this._toBool(raw);
  }

  async getAppEmbedStatus() {
    // It returns "themeEnabled" state (Shopify), which come as "false"/"true" STRING
    const res = await getThemeEnabled(); // GET /api/shops/themeEnabled
    const raw = this._unwrapData(res);
    const themeEnabled = this._toBool(raw);

    // A DEFINITIVE answer is only 'true'/'false' (or a real boolean). Right after a
    // reinstall the new offline token can lag, so the backend's theme query returns
    // '' or 'null' (no theme data) — which _toBool silently coerces to a wrong
    // boolean. Flag that so the caller can retry instead of locking onto it.
    const resolved =
      typeof raw === 'boolean' ||
      (typeof raw === 'string' && ['true', 'false', '1', '0'].includes(raw.trim().toLowerCase()));

    // Shopify returns a value that needs to be inverted for our "embedEnabled" meaning.
    // When themeEnabled is false => embed is enabled.
    return { themeEnabled, resolved };
  }

  async setComingSoonClicked(dto) {
    try {
      return await setComingSoonClicked(dto);
    } catch (e) {
      return null;
    }
  }
}

export const api = new Api(baseUrl);
